import React, { useState } from 'react';
import SectionDivider from './SectionDivider';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  date: '',
  time: '',
  guests: 2,
  notes: '',
};

const inputClasses =
  'w-full bg-white border border-gold/40 rounded-xl px-3 py-2 text-coffee placeholder:text-coffee/40 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition';
const labelClasses = 'block text-sm font-medium text-coffee/80 mb-1';

const CLOSED_WEEKDAY = 2; // Tuesday
const OPEN_TIME = '16:00';
const CLOSE_TIME = '23:00';
const BREAK_START_DATE = '2026-08-12';
const BREAK_END_DATE = '2026-08-18';

function isBreakDate(dateStr) {
  return dateStr >= BREAK_START_DATE && dateStr <= BREAK_END_DATE;
}

function isClosedDate(dateStr) {
  if (!dateStr) return false;
  return isBreakDate(dateStr) || new Date(`${dateStr}T00:00:00`).getDay() === CLOSED_WEEKDAY;
}

function isOutsideHours(timeStr) {
  if (!timeStr) return false;
  return timeStr < OPEN_TIME || timeStr > CLOSE_TIME;
}

function ReservationModal({ open, onClose }) {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('');

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    setStatus('idle');
    setErrorMessage('');
    setForm(initialForm);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (isBreakDate(form.date)) {
      setStatus('error');
      setErrorMessage("We're closed for a summer break from 12 to 18 August. Please choose another date.");
      return;
    }
    if (isClosedDate(form.date)) {
      setStatus('error');
      setErrorMessage("We're closed on Tuesdays — please pick another day.");
      return;
    }
    if (isOutsideHours(form.time)) {
      setStatus('error');
      setErrorMessage('Please choose a time between 16:00 and 23:00, our opening hours.');
      return;
    }

    setStatus('submitting');

    try {
      const res = await fetch('/api/reserve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }

      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-coffee/70 backdrop-blur-sm p-4">
      <div className="bg-cream rounded-3xl shadow-2xl ring-1 ring-gold/30 max-w-md w-full relative max-h-[90vh] overflow-y-auto">
        <div className="flex h-1.5 w-full rounded-t-3xl overflow-hidden">
          <div className="flex-1 bg-flag-green" />
          <div className="flex-1 bg-flag-yellow" />
          <div className="flex-1 bg-flag-red" />
        </div>

        <div className="p-6">
          <button
            onClick={handleClose}
            aria-label="Close"
            className="absolute top-4 right-4 text-coffee/40 hover:text-terracotta text-2xl leading-none transition"
          >
            &times;
          </button>

          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl text-terracotta">&#10003;</span>
              </div>
              <h3 className="font-display text-2xl font-semibold text-coffee mb-3">Reservation Received!</h3>
              <p className="text-coffee/70">
                Thank you, {form.name.split(' ')[0] || 'guest'}. We've sent a confirmation to{' '}
                <span className="font-medium">{form.email}</span>. We look forward to seeing you.
              </p>
              <button
                onClick={handleClose}
                className="mt-6 bg-terracotta hover:bg-terracotta-dark text-cream font-medium px-6 py-2 rounded-full transition"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <h3 className="font-display text-2xl font-semibold text-coffee text-center mb-1">Reserve a Table</h3>
              <p className="text-coffee/60 text-sm text-center">Aregash Ethiopian Restaurant, Den Haag</p>
              <p className="text-coffee/50 text-xs text-center mb-2">Open 16:00–23:00 · Closed Tuesdays</p>
              <SectionDivider className="!py-3 scale-90" />

              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                <div>
                  <label className={labelClasses}>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    className={inputClasses}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelClasses}>Email</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={form.phone}
                      onChange={handleChange}
                      className={inputClasses}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelClasses}>Date</label>
                    <input
                      type="date"
                      name="date"
                      required
                      min={new Date().toISOString().split('T')[0]}
                      value={form.date}
                      onChange={handleChange}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>Time</label>
                    <input
                      type="time"
                      name="time"
                      required
                      min={OPEN_TIME}
                      max={CLOSE_TIME}
                      value={form.time}
                      onChange={handleChange}
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className={labelClasses}>Guests</label>
                    <input
                      type="number"
                      name="guests"
                      min={1}
                      max={20}
                      required
                      value={form.guests}
                      onChange={handleChange}
                      className={inputClasses}
                    />
                  </div>
                </div>

                {isBreakDate(form.date) && (
                  <p className="text-red-700 text-sm">We're closed for a summer break from 12 to 18 August. Please choose another date.</p>
                )}
                {!isBreakDate(form.date) && isClosedDate(form.date) && (
                  <p className="text-red-700 text-sm">We're closed on Tuesdays — please pick another day.</p>
                )}
                {!isClosedDate(form.date) && isOutsideHours(form.time) && (
                  <p className="text-red-700 text-sm">Please choose a time between 16:00 and 23:00.</p>
                )}

                <div>
                  <label className={labelClasses}>
                    Special Requests <span className="text-coffee/40">(optional)</span>
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    value={form.notes}
                    onChange={handleChange}
                    className={inputClasses}
                    placeholder="Allergies, occasion, seating preference..."
                  />
                </div>

                {status === 'error' && (
                  <p className="text-red-700 text-sm">{errorMessage}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting' || isClosedDate(form.date) || isOutsideHours(form.time)}
                  className="w-full bg-terracotta hover:bg-terracotta-dark disabled:opacity-60 text-cream font-medium px-6 py-3 rounded-full shadow-md transition"
                >
                  {status === 'submitting' ? 'Sending...' : 'Confirm Reservation'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReservationModal;
